import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import { useNavigate, useSearchParams } from "react-router-dom"
import {
  type DashboardAlert,
  getDashboardAgentOperations,
  listDashboardAlerts,
  getDashboardKpis,
  getDashboardKycPipeline,
  getDashboardMyPerformance,
  getDashboardMyWork,
  getDashboardPortfolio,
  getDashboardReportsSummary,
  getDashboardVerification,
} from "../api/endpoints/dashboard"
import { listAuditLogs } from "../api/endpoints/audit"
import { listSystemConfig } from "../api/endpoints/system_config"
import { getSystemHealth } from "../api/endpoints/system_health"
import { listTargets } from "../api/endpoints/targets"
import { listTickets } from "../api/endpoints/tickets"
import { generateReport } from "../api/endpoints/reports"
import {
  Button,
  CardSkeleton,
  DataTable,
  EmptyState,
  FormField,
  Input,
  Select,
  StatusPill,
  TableBody,
  TableHead,
  TableRow,
  Td,
  Th,
} from "../components/ui"
import { DownloadIcon, RefreshIcon } from "../components/icons"
import { useAuth } from "../stores/auth"
import { formatCurrency, formatDate, formatDateTime, formatEnumLabel } from "../utils/formatting"

const CHART_COLORS = ["#0f766e", "#2563eb", "#d97706", "#dc2626", "#7c3aed", "#475569"]
const SUPER_ADMIN_POLICY_KEYS = [
  "otp_expiry_seconds",
  "session_ttl_admin",
  "audit_retention_days",
  "portal_payment_timeout_seconds",
]

function Panel({
  title,
  subtitle,
  children,
}: {
  title: string
  subtitle?: string
  children: React.ReactNode
}) {
  return (
    <section className="bg-white rounded-lg border border-slate-200 shadow-sm p-5">
      <div className="mb-4">
        <h3 className="text-sm font-bold text-slate-800">{title}</h3>
        {subtitle ? <p className="text-xs text-slate-500 mt-1">{subtitle}</p> : null}
      </div>
      {children}
    </section>
  )
}

function StatTile({
  label,
  value,
  tone = "slate",
  onClick,
}: {
  label: string
  value: string | number
  tone?: "slate" | "emerald" | "blue" | "amber" | "red"
  onClick?: () => void
}) {
  const toneMap: Record<string, string> = {
    slate: "bg-slate-50 text-slate-800",
    emerald: "bg-emerald-50 text-emerald-700",
    blue: "bg-blue-50 text-blue-700",
    amber: "bg-amber-50 text-amber-700",
    red: "bg-red-50 text-red-700",
  }

  const content = (
    <>
      <p className="text-[11px] uppercase tracking-wide font-semibold opacity-80">{label}</p>
      <p className="text-xl font-bold mt-2">{value}</p>
    </>
  )

  if (onClick) {
    return (
      <button
        type="button"
        className={`w-full rounded-lg border border-slate-100 p-4 text-left ${toneMap[tone]}`}
        onClick={onClick}
      >
        {content}
      </button>
    )
  }

  return <div className={`rounded-lg border border-slate-100 p-4 ${toneMap[tone]}`}>{content}</div>
}

function ticketPriorityTone(
  priority?: string,
): "neutral" | "success" | "warning" | "danger" | "info" {
  switch (priority) {
    case "URGENT":
      return "danger"
    case "HIGH":
      return "warning"
    case "MEDIUM":
      return "info"
    default:
      return "neutral"
  }
}

function ticketStatusTone(
  status?: string,
): "neutral" | "success" | "warning" | "danger" | "info" {
  switch (status) {
    case "RESOLVED":
      return "success"
    case "CLOSED":
      return "neutral"
    case "IN_PROGRESS":
      return "info"
    case "OPEN":
    case "AWAITING_INFO":
      return "warning"
    default:
      return "neutral"
  }
}

function serviceStatusTone(status?: string): "success" | "warning" | "danger" {
  if (status === "degraded") {
    return "warning"
  }

  if (status === "down" || status === "critical") {
    return "danger"
  }

  return "success"
}

function formatServiceStatus(status?: string) {
  if (!status) {
    return "Unknown"
  }

  return status.replace(/_/g, " ")
}

function summarizePolicySnapshot(configs: any[]) {
  const configMap = new Map(configs.map((item) => [item.key, item]))
  const prioritized = SUPER_ADMIN_POLICY_KEYS.map((key) => configMap.get(key)).filter(Boolean)
  const fallback = configs.filter((item) => !SUPER_ADMIN_POLICY_KEYS.includes(item.key)).slice(0, 4)

  return [...prioritized, ...fallback].slice(0, 4)
}

function DashboardFilters({
  searchParams,
  setSearchParams,
}: {
  searchParams: URLSearchParams
  setSearchParams: (nextInit: URLSearchParams) => void
}) {
  const update = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams)
    if (value) {
      next.set(key, value)
    } else {
      next.delete(key)
    }
    setSearchParams(next)
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
      <FormField label="Date From">
        <Input
          type="date"
          value={searchParams.get("date_from") ?? ""}
          onChange={(event) => update("date_from", event.target.value)}
        />
      </FormField>
      <FormField label="Date To">
        <Input
          type="date"
          value={searchParams.get("date_to") ?? ""}
          onChange={(event) => update("date_to", event.target.value)}
        />
      </FormField>
      <FormField label="Region">
        <Input
          placeholder="All regions"
          value={searchParams.get("region") ?? ""}
          onChange={(event) => update("region", event.target.value)}
        />
      </FormField>
      <FormField label="Team">
        <Input
          placeholder="All teams"
          value={searchParams.get("team_name") ?? ""}
          onChange={(event) => update("team_name", event.target.value)}
        />
      </FormField>
    </div>
  )
}

function renderTable(rows: Array<Record<string, any>>, emptyLabel: string) {
  if (!rows.length) {
    return <EmptyState title={emptyLabel} description="No records matched the current filters." />
  }

  const headers = Object.keys(rows[0] ?? {})
  return (
    <div className="overflow-x-auto">
      <DataTable>
        <TableHead>
          {headers.map((header) => (
            <Th key={header}>{header.replace(/_/g, " ")}</Th>
          ))}
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={index}>
              {headers.map((header) => (
                <Td key={header}>{String(row[header] ?? "-")}</Td>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </DataTable>
    </div>
  )
}

export function DashboardPage() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  if (!user) {
    return null
  }

  const filterParams = Object.fromEntries(searchParams.entries())
  const isSuperAdmin = user.role === "SUPER_ADMIN"
  const isManagement = ["SUPER_ADMIN", "ADMIN"].includes(user.role)
  const isAdmin = user.role === "ADMIN"
  const isOpsManager = user.role === "OPS_MANAGER"
  const isAgent = user.role === "AGENT"
  const isVerification = user.role === "VERIFICATION_STAFF"

  const alertsQuery = useQuery({
    queryKey: ["dashboard-alerts", filterParams, user.role],
    queryFn: () => listDashboardAlerts(filterParams),
  })

  const kpisQuery = useQuery({
    queryKey: ["dashboard-kpis", filterParams, user.role],
    queryFn: () => getDashboardKpis(filterParams),
  })

  const portfolioQuery = useQuery({
    queryKey: ["dashboard-portfolio", filterParams, user.role],
    queryFn: () => getDashboardPortfolio(filterParams),
    enabled: isManagement || isOpsManager,
  })

  const agentOpsQuery = useQuery({
    queryKey: ["dashboard-agent-ops", filterParams, user.role],
    queryFn: () => getDashboardAgentOperations(filterParams),
    enabled: isManagement || isOpsManager,
  })

  const kycPipelineQuery = useQuery({
    queryKey: ["dashboard-kyc", filterParams, user.role],
    queryFn: () => getDashboardKycPipeline(filterParams),
    enabled: isManagement || isOpsManager || isVerification,
  })

  const reportsSummaryQuery = useQuery({
    queryKey: ["dashboard-reports-summary", user.role],
    queryFn: () => getDashboardReportsSummary(),
    enabled: isManagement || isOpsManager,
  })

  const targetsQuery = useQuery({
    queryKey: ["dashboard-admin-targets"],
    queryFn: () => listTargets({ per_page: "20" }),
    enabled: isAdmin,
  })

  const ticketsQuery = useQuery({
    queryKey: ["dashboard-ticket-watch", user.role],
    queryFn: () =>
      listTickets({
        status: "OPEN,IN_PROGRESS,AWAITING_INFO",
        per_page: "5",
      }),
    enabled: isManagement || isOpsManager,
  })

  const myWorkQuery = useQuery({
    queryKey: ["dashboard-my-work"],
    queryFn: () => getDashboardMyWork(),
    enabled: isAgent,
  })

  const myPerformanceQuery = useQuery({
    queryKey: ["dashboard-my-performance", filterParams],
    queryFn: () => getDashboardMyPerformance(filterParams),
    enabled: isAgent,
  })

  const verificationQuery = useQuery({
    queryKey: ["dashboard-verification"],
    queryFn: () => getDashboardVerification(),
    enabled: isVerification,
  })

  const systemHealthQuery = useQuery({
    queryKey: ["dashboard-system-health"],
    queryFn: getSystemHealth,
    enabled: isSuperAdmin,
    refetchInterval: 30_000,
  })

  const systemConfigQuery = useQuery({
    queryKey: ["dashboard-system-config"],
    queryFn: listSystemConfig,
    enabled: isSuperAdmin,
  })

  const auditFeedQuery = useQuery({
    queryKey: ["dashboard-audit-feed"],
    queryFn: () => listAuditLogs({ per_page: "6" }),
    enabled: isSuperAdmin,
  })

  const exportMutation = useMutation({
    mutationFn: (format: "csv" | "pdf") =>
      generateReport({
        templateKey: "DASHBOARD_SNAPSHOT",
        format,
        delivery: "download",
        filters: filterParams,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["dashboard-reports-summary"] })
    },
  })

  const alerts = alertsQuery.data?.data ?? []
  const kpis = kpisQuery.data?.data
  const portfolio = portfolioQuery.data?.data
  const microLoanSummary = portfolio?.microLoanSummary
  const agentOps = agentOpsQuery.data?.data
  const kycPipeline = kycPipelineQuery.data?.data
  const reportsSummary = reportsSummaryQuery.data?.data
  const adminTargets = targetsQuery.data?.data ?? []
  const ticketWatch = ticketsQuery.data?.data ?? []
  const myWork = myWorkQuery.data?.data
  const myPerformance = myPerformanceQuery.data?.data
  const verification = verificationQuery.data?.data
  const systemHealth = systemHealthQuery.data?.data
  const configSnapshot = systemConfigQuery.data?.data ?? []
  const recentAuditFeed = auditFeedQuery.data?.data ?? []
  const adminTargetSummary = {
    active: adminTargets.filter((target: any) => target.isActive).length,
    onTarget: adminTargets.filter((target: any) => target.progress?.status === "GREEN").length,
    belowTarget: adminTargets.filter((target: any) => target.progress?.status === "RED").length,
  }
  const systemServices = systemHealth?.services ?? []
  const policySnapshot = summarizePolicySnapshot(configSnapshot)
  const recentFailures = systemHealth?.recentFailures ?? []
  const superAdminKpis = isSuperAdmin
    ? [
        {
          label: "Total Portfolio",
          value: formatCurrency(kpis?.summary?.outstandingAmount ?? 0),
          tone: "blue" as const,
        },
        {
          label: "Recovered",
          value: formatCurrency(kpis?.summary?.recoveredAmount ?? 0),
          tone: "emerald" as const,
        },
        {
          label: "Pending Auth",
          value: `${kpis?.summary?.pendingDisbursementCount ?? 0} / ${formatCurrency(
            kpis?.summary?.pendingDisbursementAmount ?? 0,
          )}`,
          tone: "amber" as const,
        },
        {
          label: "Overdue Loans",
          value: kpis?.summary?.overdueLoans ?? 0,
          tone: "red" as const,
        },
        {
          label: "Category Z",
          value: `${kpis?.summary?.categoryZCount ?? 0} / ${formatCurrency(
            kpis?.summary?.categoryZAmount ?? 0,
          )}`,
          tone: "red" as const,
        },
        {
          label: "Pending KYC",
          value: kpis?.summary?.pendingKyc ?? 0,
          tone: "amber" as const,
        },
        {
          label: "Active Loans",
          value: kpis?.summary?.activeLoans ?? 0,
          tone: "slate" as const,
        },
        {
          label: "Open Failures",
          value: recentFailures.reduce(
            (total: number, item: any) => total + (Number(item.count) || 0),
            0,
          ),
          tone: recentFailures.length > 0 ? ("red" as const) : ("slate" as const),
        },
      ]
    : []

  return (
    <div className="space-y-6">
      <section className="bg-white rounded-lg border border-slate-200 shadow-sm p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">
              {user.role.replace(/_/g, " ")} Dashboard
            </p>
            <h1 className="text-2xl font-bold text-slate-900 mt-2">
              {isSuperAdmin
                ? "Enterprise loan operations and system control"
                : isAdmin
                  ? "Admin operations control center"
                  : "Operational reporting in one place"}
            </h1>
            <p className="text-sm text-slate-500 mt-2 max-w-2xl">
              {isSuperAdmin
                ? "Track financial controls, category Z exposure, operational failures, and governance activity from one dashboard without changing existing role workflows."
                : isAdmin
                ? "Use quick actions to move between assignments, tickets, KYC monitoring, and report generation without losing filter context."
                : "Filters persist in the URL so the same slice carries into report generation and drill-down work."}
            </p>
          </div>
            <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              onClick={() =>
                void queryClient.invalidateQueries({
                  predicate: (query) => String(query.queryKey[0] ?? "").startsWith("dashboard"),
                })
              }
            >
              <RefreshIcon className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            {user.permissions?.canGenerateReports ? (
              <>
                {isOpsManager ? (
                  <Button variant="secondary" onClick={() => navigate("/tickets")}>
                    Raise Ticket
                  </Button>
                ) : null}
                <Button
                  variant="outline"
                  onClick={() => exportMutation.mutate("csv")}
                  disabled={exportMutation.isPending}
                >
                  <DownloadIcon className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
                <Button onClick={() => exportMutation.mutate("pdf")} disabled={exportMutation.isPending}>
                  <DownloadIcon className="h-4 w-4 mr-2" />
                  Export PDF
                </Button>
              </>
            ) : null}
            {isSuperAdmin ? (
              <>
                <Button variant="secondary" onClick={() => navigate("/disbursements")}>
                  Disbursements
                </Button>
                <Button variant="secondary" onClick={() => navigate("/system-health")}>
                  System Health
                </Button>
              </>
            ) : null}
          </div>
        </div>
        <div className="mt-5">
          <DashboardFilters searchParams={searchParams} setSearchParams={setSearchParams} />
        </div>
      </section>

      <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {alertsQuery.isLoading
          ? Array.from({ length: 4 }).map((_, index) => <CardSkeleton key={index} />)
          : alerts.map((alert: DashboardAlert) => (
              <StatTile
                key={alert.key}
                label={alert.title}
                value={alert.amount ? `${alert.count} / ${formatCurrency(alert.amount)}` : alert.count}
                tone={
                  alert.variant === "danger"
                    ? "red"
                    : alert.variant === "warning"
                      ? "amber"
                      : alert.variant === "info"
                        ? "blue"
                        : "slate"
                }
                onClick={alert.actionPath ? () => navigate(alert.actionPath!) : undefined}
              />
            ))}
      </section>

      {isSuperAdmin ? (
      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {superAdminKpis.map((item) => (
            <StatTile key={item.label} label={item.label} value={item.value} tone={item.tone} />
          ))}
        </section>
      ) : null}

      {((isManagement && !isSuperAdmin) || isOpsManager) && kpis?.summary ? (
        <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatTile
            label={isOpsManager ? "Outstanding" : "Active Loans"}
            value={
              isOpsManager
                ? formatCurrency(kpis.summary.outstandingAmount ?? 0)
                : (kpis.summary.activeLoans ?? 0)
            }
            tone={isOpsManager ? "blue" : "slate"}
          />
          <StatTile
            label={isOpsManager ? "Recovered" : "Outstanding"}
            value={
              isOpsManager
                ? formatCurrency(kpis.summary.recoveredAmount ?? 0)
                : formatCurrency(kpis.summary.outstandingAmount ?? 0)
            }
            tone={isOpsManager ? "emerald" : "blue"}
          />
          <StatTile
            label={isOpsManager ? "Overdue Loans" : "Recovered"}
            value={
              isOpsManager
                ? (kpis.summary.overdueLoans ?? 0)
                : formatCurrency(kpis.summary.recoveredAmount ?? 0)
            }
            tone={isOpsManager ? "amber" : "emerald"}
          />
          <StatTile
            label={isOpsManager ? "Pending KYC" : "Pending KYC"}
            value={kpis.summary.pendingKyc ?? 0}
            tone="amber"
          />
        </section>
      ) : null}

      {isAgent && kpis?.summary ? (
        <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
          <StatTile label="Assigned Clients" value={kpis.summary.assignedClients ?? 0} />
          <StatTile
            label="Outstanding"
            value={formatCurrency(kpis.summary.outstandingAmount ?? 0)}
            tone="blue"
          />
          <StatTile
            label="Recovered"
            value={formatCurrency(kpis.summary.recoveredAmount ?? 0)}
            tone="emerald"
          />
          <StatTile label="Interactions" value={kpis.summary.interactionsLogged ?? 0} tone="amber" />
          <StatTile label="Resolved" value={kpis.summary.clientsResolved ?? 0} tone="slate" />
        </section>
      ) : null}

      {isVerification && verification?.queueSummary ? (
        <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatTile label="Pending Reviews" value={verification.queueSummary.pendingReviews ?? 0} tone="amber" />
          <StatTile label="Approved Reviews" value={verification.queueSummary.approvedReviews ?? 0} tone="emerald" />
          <StatTile label="Rejected Reviews" value={verification.queueSummary.rejectedReviews ?? 0} tone="red" />
        </section>
      ) : null}

      {isSuperAdmin ? (
        <>
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <Panel
              title="Quick Actions"
              subtitle="Jump directly into the privileged Super Admin workflows."
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Button variant="secondary" onClick={() => navigate("/disbursements")}>
                  Review Disbursements
                </Button>
                <Button variant="secondary" onClick={() => navigate("/clients?overdue_category=CATEGORY_Z")}>
                  Category Z Queue
                </Button>
                <Button variant="secondary" onClick={() => navigate("/system-config")}>
                  System Configuration
                </Button>
                <Button variant="secondary" onClick={() => navigate("/system-health")}>
                  System Health
                </Button>
                <Button variant="secondary" onClick={() => navigate("/audit-logs")}>
                  Audit Logs
                </Button>
                <Button variant="secondary" onClick={() => navigate("/reports")}>
                  Targets & Reports
                </Button>
              </div>
            </Panel>

            <Panel
              title="System Health Snapshot"
              subtitle="Live service status and recent job failures."
            >
              {systemHealthQuery.isLoading ? (
                <CardSkeleton />
              ) : systemServices.length === 0 ? (
                <EmptyState
                  title="No health signals"
                  description="System health data is not available yet."
                />
              ) : (
                <div className="space-y-3">
                  {systemServices.slice(0, 4).map((service: any) => (
                    <button
                      key={service.name}
                      type="button"
                      className="w-full rounded-lg border border-slate-200 px-4 py-3 text-left hover:bg-slate-50"
                      onClick={() => navigate("/system-health")}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{service.name}</p>
                          <p className="text-xs text-slate-500">
                            {service.latencyMs}ms · checked {formatDateTime(service.lastChecked)}
                          </p>
                        </div>
                        <StatusPill tone={serviceStatusTone(service.status)}>
                          {formatServiceStatus(service.status)}
                        </StatusPill>
                      </div>
                    </button>
                  ))}
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                    Recent failures in the last 24h:{" "}
                    <strong>{recentFailures.reduce((total: number, item: any) => total + item.count, 0)}</strong>
                  </div>
                </div>
              )}
            </Panel>

            <Panel
              title="System Policy Snapshot"
              subtitle="A compact view of live settings tied to governance and timing."
            >
              {systemConfigQuery.isLoading ? (
                <CardSkeleton />
              ) : policySnapshot.length === 0 ? (
                <EmptyState
                  title="No configuration rows"
                  description="No live configuration values are available."
                />
              ) : (
                <div className="space-y-3">
                  {policySnapshot.map((item: any) => (
                    <div
                      key={item.key}
                      className="flex items-start justify-between gap-4 rounded-lg border border-slate-200 px-4 py-3"
                    >
                      <div>
                        <p className="text-sm font-semibold text-slate-900">
                          {item.key.replace(/_/g, " ")}
                        </p>
                        <p className="text-xs text-slate-500">
                          {item.updatedAt ? `Updated ${formatDateTime(item.updatedAt)}` : "Not yet updated"}
                        </p>
                      </div>
                      <code className="rounded bg-slate-100 px-2 py-1 text-xs text-slate-700">
                        {String(item.value)}
                      </code>
                    </div>
                  ))}
                  <Button variant="secondary" onClick={() => navigate("/system-config")}>
                    Open Configuration
                  </Button>
                </div>
              )}
            </Panel>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <Panel
              title="Priority Queues"
              subtitle="Financial controls and loss-prevention queues needing Super Admin attention."
            >
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <StatTile
                  label="Pending Authorizations"
                  value={kpis?.summary?.pendingDisbursementCount ?? 0}
                  tone="amber"
                  onClick={() => navigate("/disbursements")}
                />
                <StatTile
                  label="Pending Value"
                  value={formatCurrency(kpis?.summary?.pendingDisbursementAmount ?? 0)}
                  tone="blue"
                  onClick={() => navigate("/disbursements")}
                />
                <StatTile
                  label="Category Z Cases"
                  value={kpis?.summary?.categoryZCount ?? 0}
                  tone="red"
                  onClick={() => navigate("/clients?overdue_category=CATEGORY_Z")}
                />
                <StatTile
                  label="Category Z Exposure"
                  value={formatCurrency(kpis?.summary?.categoryZAmount ?? 0)}
                  tone="red"
                  onClick={() => navigate("/clients?overdue_category=CATEGORY_Z")}
                />
              </div>
              <div className="mt-4 space-y-2">
                {recentFailures.length === 0 ? (
                  <p className="text-sm text-slate-500">No recent failures detected in the last 24 hours.</p>
                ) : (
                  recentFailures.slice(0, 4).map((failure: any) => (
                    <div
                      key={failure.type}
                      className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3"
                    >
                      <span className="text-sm text-slate-700">{failure.type.replace(/_/g, " ")}</span>
                      <div className="flex items-center gap-3">
                        <StatusPill tone="danger">{failure.count}</StatusPill>
                        <span className="text-xs text-slate-500">
                          {failure.lastAt ? formatDateTime(failure.lastAt) : "-"}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </Panel>

            <Panel
              title="Audit Feed"
              subtitle="Recent governance and platform actions from the audit log."
            >
              {auditFeedQuery.isLoading ? (
                <CardSkeleton />
              ) : recentAuditFeed.length === 0 ? (
                <EmptyState
                  title="No audit events"
                  description="Recent audit events will appear here."
                />
              ) : (
                <div className="space-y-3">
                  {recentAuditFeed.map((row: any) => (
                    <button
                      key={row.auditId}
                      type="button"
                      className="w-full rounded-lg border border-slate-200 px-4 py-3 text-left hover:bg-slate-50"
                      onClick={() =>
                        navigate(
                          row.resourceId
                            ? `/audit-logs?search=${encodeURIComponent(row.resourceId)}`
                            : "/audit-logs",
                        )
                      }
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">
                            {row.actionType}
                          </p>
                          <p className="text-xs text-slate-500">
                            {row.actorName ?? row.actorId} · {row.resourceType} {row.resourceId ?? ""}
                          </p>
                        </div>
                        <StatusPill
                          tone={
                            row.severity === "HIGH_RISK"
                              ? "danger"
                              : row.severity === "ELEVATED"
                                ? "warning"
                                : "neutral"
                          }
                        >
                          {row.severity ?? "NORMAL"}
                        </StatusPill>
                      </div>
                      <p className="mt-2 text-xs text-slate-500">
                        {row.description ?? "No description"} · {formatDateTime(row.timestamp)}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </Panel>
          </div>
        </>
      ) : null}

      {isAdmin ? (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <Panel title="Quick Actions" subtitle="Jump straight into the Admin operational flows.">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Button variant="secondary" onClick={() => navigate("/targets?tab=assignments")}>
                Manage Assignments
              </Button>
              <Button variant="secondary" onClick={() => navigate("/agent-performance")}>
                View Agent Performance
              </Button>
              <Button variant="secondary" onClick={() => navigate("/collections?is_assigned=false")}>
                Inspect Unassigned Queue
              </Button>
              <Button variant="secondary" onClick={() => navigate("/tickets?status=OPEN")}>
                Open Tickets
              </Button>
              <Button variant="secondary" onClick={() => navigate("/verification")}>
                Monitor KYC
              </Button>
              <Button variant="secondary" onClick={() => navigate("/users")}>
                Manage Users
              </Button>
            </div>
          </Panel>

          <Panel title="Assignment Pressure" subtitle="Keep the queue balanced across active agents.">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <StatTile
                label="Unassigned Clients"
                value={agentOps?.unassignedQueue?.loanCount ?? 0}
                tone="red"
              />
              <StatTile
                label="Unassigned Exposure"
                value={formatCurrency(agentOps?.unassignedQueue?.outstandingAmount ?? 0)}
                tone="amber"
              />
              <StatTile
                label="Agents In Scope"
                value={agentOps?.leaderboard?.length ?? 0}
                tone="blue"
              />
              <StatTile
                label="Pipeline States"
                value={agentOps?.pipelineMix?.length ?? 0}
                tone="slate"
              />
            </div>
          </Panel>

          <Panel title="Target Snapshot" subtitle="Current Admin-visible recovery targets only.">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <StatTile label="Active" value={adminTargetSummary.active} tone="blue" />
              <StatTile label="On Target" value={adminTargetSummary.onTarget} tone="emerald" />
              <StatTile
                label="Below Target"
                value={adminTargetSummary.belowTarget}
                tone="red"
                onClick={() => navigate("/agent-performance?band=RED")}
              />
            </div>
            <div className="mt-4">
              {adminTargets.length === 0 ? (
                <EmptyState
                  title="No active Admin targets"
                  description="Create a target or open the targets page to manage assignment coverage."
                />
              ) : (
                renderTable(
                  adminTargets.slice(0, 5).map((target: any) => ({
                    name: target.name,
                    scope: target.scopeType === "TEAM" ? target.teamName ?? "Team" : target.agentName ?? "Agent",
                    progress: `${target.progress?.actualPercentage ?? 0}%`,
                    status: target.progress?.status ?? "PENDING",
                  })),
                  "No target rows",
                )
              )}
            </div>
          </Panel>

          <Panel title="Ticket Watch" subtitle="Open and in-progress tickets needing Admin attention.">
            {ticketWatch.length === 0 ? (
              <EmptyState
                title="No open tickets"
                description="No tickets are currently open or awaiting Admin action."
              />
            ) : (
              <div className="space-y-3">
                {ticketWatch.map((ticket: any) => (
                  <button
                    key={ticket.ticketId}
                    type="button"
                    className="w-full rounded-lg border border-slate-200 px-4 py-3 text-left hover:bg-slate-50"
                    onClick={() => navigate(`/tickets?selected_ticket_id=${ticket.ticketId}`)}
                  >
                    <div className="flex items-center gap-2">
                      <strong className="text-sm text-slate-900">{ticket.ticketId}</strong>
                      <StatusPill tone={ticketStatusTone(ticket.status)}>
                        {formatEnumLabel(ticket.status)}
                      </StatusPill>
                      <StatusPill tone={ticketPriorityTone(ticket.priority)}>
                        {ticket.priority}
                      </StatusPill>
                    </div>
                    <p className="mt-2 text-sm text-slate-700">{ticket.subject}</p>
                    <p className="mt-1 text-xs text-slate-500">
                      {ticket.assignedToName ?? "Unassigned"} · Updated {formatDateTime(ticket.updatedAt)}
                    </p>
                  </button>
                ))}
              </div>
            )}
          </Panel>
        </div>
      ) : null}

      {isManagement ? (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <Panel
            title="Micro Loan Book"
            subtitle="Quick-loan exposure, month-to-date tier movement, and current tier mix."
          >
            {portfolioQuery.isLoading ? (
              <CardSkeleton />
            ) : !microLoanSummary ? (
              <EmptyState
                title="No micro-loan summary"
                description="Portfolio summary data is not available for the selected filter set."
              />
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <StatTile label="Active Loans" value={microLoanSummary.activeLoans} tone="blue" />
                  <StatTile
                    label="Portfolio Value"
                    value={formatCurrency(microLoanSummary.portfolioValueUsd)}
                    tone="slate"
                  />
                  <StatTile
                    label="Overdue Rate"
                    value={`${microLoanSummary.overdueRate.toFixed(1)}%`}
                    tone={microLoanSummary.overdueRate > 0 ? "amber" : "emerald"}
                  />
                  <StatTile label="Promotions MTD" value={microLoanSummary.promotionsMtd} tone="emerald" />
                  <StatTile label="Demotions MTD" value={microLoanSummary.demotionsMtd} tone="red" />
                </div>

                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">Tier distribution</p>
                      <p className="text-xs text-slate-500">
                        Current client tier state across the filtered micro-loan book.
                      </p>
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => navigate("/clients?loan_type=MICRO")}
                    >
                      Open Micro Clients
                    </Button>
                  </div>

                  {microLoanSummary.tierDistribution.length === 0 ? (
                    <p className="mt-4 text-sm text-slate-500">No tiered micro-loan clients found.</p>
                  ) : (
                    <div className="mt-4 space-y-3">
                      {microLoanSummary.tierDistribution.map((row) => (
                        <div key={row.tier}>
                          <div className="flex items-center justify-between gap-3 text-sm">
                            <div className="font-semibold text-slate-800">{row.tier}</div>
                            <div className="text-slate-500">
                              {row.clientCount} clients · {row.percentage.toFixed(1)}%
                            </div>
                          </div>
                          <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-200">
                            <div
                              className="h-full rounded-full bg-teal-600"
                              style={{ width: `${Math.min(row.percentage, 100)}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </Panel>

          <Panel title="Portfolio Trend" subtitle="Rollups when available, live fallback otherwise.">
            {portfolioQuery.isLoading ? (
              <CardSkeleton />
            ) : (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={portfolio?.trend ?? []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="metricDate" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line dataKey="outstandingAmount" stroke="#2563eb" strokeWidth={2} name="Outstanding" />
                    <Line dataKey="recoveredAmount" stroke="#0f766e" strokeWidth={2} name="Recovered" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </Panel>

          <Panel title="Portfolio Distribution" subtitle="Current exposure split by overdue bucket.">
            {portfolioQuery.isLoading ? (
              <CardSkeleton />
            ) : (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={portfolio?.distribution ?? []}
                      dataKey="outstandingAmount"
                      nameKey="bucket"
                      outerRadius={96}
                      label
                    >
                      {(portfolio?.distribution ?? []).map((_: any, index: number) => (
                        <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </Panel>

          <Panel title="Agent Leaderboard" subtitle="Recovery output and workload by agent.">
            {renderTable(agentOps?.leaderboard ?? [], "No agent performance rows")}
          </Panel>

          <Panel title="KYC Pipeline" subtitle="Verification volume and aging overview.">
            {kycPipelineQuery.isLoading ? (
              <CardSkeleton />
            ) : (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={kycPipeline?.summary ?? []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="kycStatus" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="clientCount" fill="#2563eb" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Panel>

          <Panel title="Report Activity" subtitle="Recent runs and upcoming schedules.">
            <div className="space-y-4">
              {renderTable(reportsSummary?.recentRuns ?? [], "No recent report runs")}
              {renderTable(reportsSummary?.schedules ?? [], "No active report schedules")}
            </div>
          </Panel>
        </div>
      ) : null}

      {isOpsManager ? (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <Panel title="Portfolio Trend" subtitle="Recovery performance across the current filtered slice.">
            {portfolioQuery.isLoading ? (
              <CardSkeleton />
            ) : (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={portfolio?.trend ?? []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="metricDate" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line dataKey="outstandingAmount" stroke="#2563eb" strokeWidth={2} name="Outstanding" />
                    <Line dataKey="recoveredAmount" stroke="#0f766e" strokeWidth={2} name="Recovered" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </Panel>

          <Panel title="Overdue Distribution" subtitle="Exposure split by overdue bucket with Category Z removed.">
            {portfolioQuery.isLoading ? (
              <CardSkeleton />
            ) : (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={portfolio?.distribution ?? []}
                      dataKey="outstandingAmount"
                      nameKey="bucket"
                      outerRadius={96}
                      label
                    >
                      {(portfolio?.distribution ?? []).map((_: any, index: number) => (
                        <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </Panel>

          <Panel title="Agent Operations" subtitle="Read-only leaderboard across collections activity.">
            {renderTable(agentOps?.leaderboard ?? [], "No agent performance rows")}
          </Panel>

          <Panel title="Escalation Signals" subtitle="Pipeline mix and payment behaviour worth escalating.">
            <div className="space-y-4">
              {renderTable(agentOps?.pipelineMix ?? [], "No pipeline mix rows")}
              {renderTable(agentOps?.paymentBehavior ?? [], "No payment behaviour rows")}
            </div>
          </Panel>

          <Panel title="KYC Pipeline" subtitle="Monitoring view only; document access remains restricted.">
            {kycPipelineQuery.isLoading ? (
              <CardSkeleton />
            ) : (
              <div className="space-y-4">
                {renderTable(kycPipeline?.summary ?? [], "No KYC summary rows")}
                {renderTable(kycPipeline?.pendingAging ?? [], "No pending aging rows")}
              </div>
            )}
          </Panel>

          <Panel title="Reports and Schedules" subtitle="Recent report activity available to the Ops Manager role.">
            <div className="space-y-4">
              {renderTable(reportsSummary?.recentRuns ?? [], "No recent report runs")}
              {renderTable(reportsSummary?.schedules ?? [], "No active report schedules")}
            </div>
          </Panel>
        </div>
      ) : null}

      {isAgent ? (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <Panel title="My Work Queue" subtitle="Due follow-ups and promise tracking.">
            <div className="space-y-4">
              {renderTable(myWork?.tasks?.followUps ?? [], "No follow-ups due")}
              {renderTable(myWork?.tasks?.expiringPromises ?? [], "No expiring promises")}
            </div>
          </Panel>

          <Panel title="Pipeline Mix" subtitle="Assigned workload by current pipeline state.">
            {myWorkQuery.isLoading ? (
              <CardSkeleton />
            ) : (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={myWork?.pipelineSummary ?? []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="pipelineStatus" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="loanCount" fill="#0f766e" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Panel>

          <Panel title="Performance History" subtitle="Rollup history for recovery and activity.">
            {myPerformanceQuery.isLoading ? (
              <CardSkeleton />
            ) : (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={myPerformance?.history ?? []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="metricDate" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line dataKey="recoveredAmount" stroke="#0f766e" strokeWidth={2} name="Recovered" />
                    <Line dataKey="interactionsLogged" stroke="#2563eb" strokeWidth={2} name="Interactions" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </Panel>

          <Panel title="Recent Activity" subtitle="Latest interaction log entries from your queue.">
            {renderTable(myWork?.recentActivity ?? [], "No recent interactions")}
          </Panel>
        </div>
      ) : null}

      {isVerification ? (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <Panel title="Verification Queue" subtitle="Current KYC status distribution.">
            {kycPipelineQuery.isLoading ? (
              <CardSkeleton />
            ) : (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={kycPipeline?.summary ?? []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="kycStatus" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="clientCount" fill="#d97706" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Panel>

          <Panel title="Pending Aging" subtitle="How long pending verification cases have been waiting.">
            {renderTable(kycPipeline?.pendingAging ?? [], "No pending aging rows")}
          </Panel>

          <Panel title="Recent Reviews" subtitle="Most recently completed verification actions.">
            {renderTable(verification?.recentReviews ?? [], "No recent reviews")}
          </Panel>

          <Panel title="Ticket Summary" subtitle="Operational tickets linked to verification work.">
            {renderTable(verification?.ticketSummary ?? [], "No verification ticket rows")}
          </Panel>
        </div>
      ) : null}

      {exportMutation.data?.data ? (
        <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-4">
          Dashboard export queued as {exportMutation.data.data.runId}. Download it from the reports history once processing completes.
        </div>
      ) : null}
    </div>
  )
}
